import { DATETIME_RFC_FORMAT, DATE_INPUT_FORMAT } from '@/constants';
import {
  DatetimeFormatEnum,
  DownloadTripWithCompletionAndRateAsCsvInput,
  TripTripWithCompletionAndRateCsvColumn,
  TripTripWithCompletionAndRateCsvColumnEnum,
  TripWithCompletionAndRateSearch,
} from '@/models/gen/graphql';
import { Dayts, QueryInputType, queryInput, saveContent } from '@/utils';

import { DownloadTripWithCompletionAndRateAsCsvDocument } from '@/api/queries';
import { TripsFiltersState } from '@/features/Trips/components/TripFilters';
import graphApi from '@/api';

// Combining these at the moment in preparation for future refactor where the filter state will match the search more closely.
export type TripWithCompletionAndRateSearchRaw = Record<keyof TripWithCompletionAndRateSearch, unknown> &
  TripsFiltersState & {
    hasCompletion?: boolean;
    hasNoDriver?: boolean;
  };

const [, runDownloadTripWithCompletionAndRateAsCSV] = graphApi(DownloadTripWithCompletionAndRateAsCsvDocument);

const downloadTripWithCompletionAndRateAsCSV = async (query: Partial<TripWithCompletionAndRateSearchRaw>[]): Promise<string> => {
  const input: DownloadTripWithCompletionAndRateAsCsvInput = {
    columns,
    customColumns,
    query: formatQueries(query),
  };
  const res = await runDownloadTripWithCompletionAndRateAsCSV({ input });
  const fileName = `Trips-${new Dayts().format(DATE_INPUT_FORMAT)}.csv`;
  const content = res?.downloadTripWithCompletionAndRateAsCSV?.data || '';
  saveContent(content, fileName);
  return content;
};

const isLoopName = (flightNumber: string) => flightNumber.length > 4 || flightNumber.match(/\D/g);

// NOTE: This contains all of the fields possible to search for with this endpoint, but only adds them to the query if the value is given. This is by design.
export const formatQueries = (rawQueries: Partial<TripWithCompletionAndRateSearchRaw>[]): TripWithCompletionAndRateSearch[] => {
  rawQueries = rawQueries.map((rawQuery) => {
    if (rawQuery?.scheduled) {
      const [from, to] = rawQuery.scheduled as string[];
      rawQuery.scheduled = [
        new Dayts(from).startOf('day').format(DATETIME_RFC_FORMAT),
        new Dayts(to).endOf('day').format(DATETIME_RFC_FORMAT),
      ];
    }
    return rawQuery;
  });
  return rawQueries
    .map((rawQuery) => ({
      accidentIssueCount: rawQuery.issueType === 'ACCIDENT' ? queryInput([0], QueryInputType.NOTIN) : undefined,
      actual: rawQuery.actual ? queryInput(rawQuery.actual, QueryInputType.OR) : undefined,
      airportCode: rawQuery.airportCode?.length ? queryInput(rawQuery.airportCode, QueryInputType.OR) : undefined,
      attendants: rawQuery.attendants ? queryInput(rawQuery.attendants, QueryInputType.OR) : undefined,
      cachedDoLocationId: rawQuery.cachedDoLocationId ? queryInput(rawQuery.cachedDoLocationId, QueryInputType.OR) : undefined,
      cachedDriverId: rawQuery.cachedDriverId ? queryInput(rawQuery.cachedDriverId, QueryInputType.OR) : undefined,
      cachedPayerProviderId: rawQuery.cachedPayerProviderId ? queryInput(rawQuery.cachedPayerProviderId, QueryInputType.OR) : undefined,
      cachedPuLocationId: rawQuery.cachedPuLocationId ? queryInput(rawQuery.cachedPuLocationId, QueryInputType.OR) : undefined,
      cachedVehicleId: rawQuery.cachedVehicleId ? queryInput(rawQuery.cachedVehicleId, QueryInputType.OR) : undefined,
      combineId: rawQuery.combineId ? queryInput(rawQuery.combineId, QueryInputType.OR) : undefined,
      combineType: rawQuery.combineType ? queryInput(rawQuery.combineType, QueryInputType.OR) : undefined,
      communicationCount: rawQuery.communicationCount ? queryInput(rawQuery.communicationCount, QueryInputType.OR) : undefined,
      companyId: rawQuery.companyId ? queryInput(rawQuery.companyId, QueryInputType.OR) : undefined,
      completionComment: rawQuery.completionComment ? queryInput(rawQuery.completionComment, QueryInputType.OR) : undefined,
      completionCost: rawQuery.completionCost ? queryInput(rawQuery.completionCost, QueryInputType.OR) : undefined,
      completionCreatedAt: rawQuery.completionCreatedAt ? queryInput(rawQuery.completionCreatedAt, QueryInputType.OR) : undefined,
      completionCreatedBy: rawQuery.completionCreatedBy ? queryInput(rawQuery.completionCreatedBy, QueryInputType.OR) : undefined,
      completionCurrency: rawQuery.completionCurrency ? queryInput(rawQuery.completionCurrency, QueryInputType.OR) : undefined,
      completionDatetime: rawQuery.completionDatetime ? queryInput(rawQuery.completionDatetime, QueryInputType.OR) : undefined,
      completionDistanceMeters: rawQuery.completionDistanceMeters
        ? queryInput(rawQuery.completionDistanceMeters, QueryInputType.OR)
        : undefined,
      completionEfficiency: rawQuery.completionEfficiency ? queryInput(rawQuery.completionEfficiency, QueryInputType.OR) : undefined,
      completionEquivalency: rawQuery.completionEquivalency ? queryInput(rawQuery.completionEquivalency, QueryInputType.OR) : undefined,
      completionId: rawQuery.hasCompletion ? queryInput([], QueryInputType.ISNOTNULL) : undefined,
      completionTypeDisplayName: rawQuery.completionTypeDisplayName
        ? queryInput(rawQuery.completionTypeDisplayName, QueryInputType.OR)
        : undefined,
      completionTypeId: rawQuery.completionTypeId ? queryInput(rawQuery.completionTypeId, QueryInputType.OR) : undefined,
      completionUpdatedAt: rawQuery.completionUpdatedAt ? queryInput(rawQuery.completionUpdatedAt, QueryInputType.OR) : undefined,
      completionUpdatedBy: rawQuery.completionUpdatedBy ? queryInput(rawQuery.completionUpdatedBy, QueryInputType.OR) : undefined,
      createdAt: rawQuery.createdAt ? queryInput(rawQuery.createdAt, QueryInputType.OR) : undefined,
      createdBy: rawQuery.createdBy ? queryInput(rawQuery.createdBy, QueryInputType.OR) : undefined,
      crewId: rawQuery.crewId ? queryInput(rawQuery.crewId, QueryInputType.OR) : undefined,
      curbsideAt: rawQuery.curbsideAt ? queryInput(rawQuery.curbsideAt, QueryInputType.OR) : undefined,
      deletedAt: rawQuery.deletedAt ? queryInput(rawQuery.deletedAt, QueryInputType.OR) : undefined,
      deletedBy: rawQuery.deletedBy ? queryInput(rawQuery.deletedBy, QueryInputType.OR) : undefined,
      dispatchIssueCount: rawQuery.issueType === 'DISPATCH' ? queryInput([0], QueryInputType.NOTIN) : undefined,
      doActualDatetime: rawQuery.doActualDatetime ? queryInput(rawQuery.doActualDatetime, QueryInputType.OR) : undefined,
      doAirportCode: rawQuery.doAirportCode ? queryInput(rawQuery.doAirportCode, QueryInputType.OR) : undefined,
      doLocationAddress: rawQuery.doLocationAddress ? queryInput(rawQuery.doLocationAddress, QueryInputType.OR) : undefined,
      doLocationCityName: rawQuery.doLocationCityName ? queryInput(rawQuery.doLocationCityName, QueryInputType.OR) : undefined,
      doLocationCoordinates: rawQuery.doLocationCoordinates ? queryInput(rawQuery.doLocationCoordinates, QueryInputType.OR) : undefined,
      doLocationId: rawQuery.doLocationId?.length ? queryInput(rawQuery.doLocationId, QueryInputType.OR) : undefined,
      doLocationName: rawQuery.doLocationName ? queryInput(rawQuery.doLocationName, QueryInputType.OR) : undefined,
      doLocationPhoneNumber: rawQuery.doLocationPhoneNumber ? queryInput(rawQuery.doLocationPhoneNumber, QueryInputType.OR) : undefined,
      doLocationStateCode: rawQuery.doLocationStateCode ? queryInput(rawQuery.doLocationStateCode, QueryInputType.OR) : undefined,
      doLocationZipCode: rawQuery.doLocationZipCode ? queryInput(rawQuery.doLocationZipCode, QueryInputType.OR) : undefined,
      driverAvatar: rawQuery.driverAvatar ? queryInput(rawQuery.driverAvatar, QueryInputType.OR) : undefined,
      driverCrewCount: rawQuery.driverCrewCount ? queryInput(rawQuery.driverCrewCount, QueryInputType.OR) : undefined,
      driverEmployeeId: rawQuery.driverEmployeeId ? queryInput(rawQuery.driverEmployeeId, QueryInputType.OR) : undefined,
      driverFirstName: rawQuery.driverFirstName ? queryInput(rawQuery.driverFirstName, QueryInputType.OR) : undefined,
      driverId: rawQuery.hasNoDriver ? queryInput([], QueryInputType.ISNULL) : undefined,
      driverLastName: rawQuery.driverLastName ? queryInput(rawQuery.driverLastName, QueryInputType.OR) : undefined,
      etaMinutes: rawQuery.etaMinutes ? queryInput(rawQuery.etaMinutes, QueryInputType.OR) : undefined,
      fcrCount: rawQuery.fcrCount ? queryInput(rawQuery.fcrCount, QueryInputType.OR) : undefined,
      firstName: rawQuery.firstName ? queryInput(rawQuery.firstName, QueryInputType.OR) : undefined,
      flagColors: rawQuery.flagColors ? queryInput(rawQuery.flagColors, QueryInputType.OR) : undefined,
      flightNumber:
        rawQuery.flightNumber && !isLoopName(rawQuery.flightNumber) ? queryInput(rawQuery.flightNumber, QueryInputType.OR) : undefined,
      fromManifest: rawQuery.fromManifest ? queryInput(rawQuery.fromManifest, QueryInputType.OR) : undefined,
      hasPaperwork: rawQuery.hasPaperwork ? queryInput(rawQuery.hasPaperwork, QueryInputType.OR) : undefined,
      id: rawQuery.id ? queryInput(rawQuery.id, QueryInputType.OR) : undefined,
      importTime: rawQuery.importTime ? queryInput(rawQuery.importTime, QueryInputType.OR) : undefined,
      incidentIssueCount: rawQuery.issueType === 'INCIDENT' ? queryInput([0], QueryInputType.NOTIN) : undefined,
      isIllegalCombine: rawQuery.isIllegalCombine ? queryInput(rawQuery.isIllegalCombine, QueryInputType.OR) : undefined,
      kind: rawQuery.kind ? queryInput(rawQuery.kind, QueryInputType.OR) : undefined,
      lastName: rawQuery.lastName ? queryInput(rawQuery.lastName, QueryInputType.OR) : undefined,
      latestScheduled: rawQuery.latestScheduled ? queryInput(rawQuery.latestScheduled, QueryInputType.OR) : undefined,
      latestScheduledUtc: rawQuery.latestScheduledUtc ? queryInput(rawQuery.latestScheduledUtc, QueryInputType.OR) : undefined,
      legacyId: rawQuery.legacyId ? queryInput(rawQuery.legacyId, QueryInputType.OR) : undefined,
      loopName:
        rawQuery.loopName || (rawQuery.flightNumber && isLoopName(rawQuery.flightNumber))
          ? queryInput(rawQuery.loopName ?? rawQuery.flightNumber, QueryInputType.OR)
          : undefined,
      lostAndFoundIssueCount: rawQuery.issueType === 'LOST_AND_FOUND' ? queryInput([0], QueryInputType.NOTIN) : undefined,
      manifestImportId: rawQuery.manifestImportId ? queryInput(rawQuery.manifestImportId, QueryInputType.OR) : undefined,
      offset: rawQuery.offset ? queryInput(rawQuery.offset, QueryInputType.OR) : undefined,
      payerProviderDisplayName: rawQuery.payerProviderDisplayName
        ? queryInput(rawQuery.payerProviderDisplayName, QueryInputType.OR)
        : undefined,
      payerProviderId: rawQuery.payerProviderId ? queryInput(rawQuery.payerProviderId, QueryInputType.OR) : undefined,
      pilots: rawQuery.pilots ? queryInput(rawQuery.pilots, QueryInputType.OR) : undefined,
      plannedAt: rawQuery.plannedAt ? queryInput(rawQuery.plannedAt, QueryInputType.OR) : undefined,
      providerId: rawQuery.providerId ? queryInput(rawQuery.providerId, QueryInputType.OR) : undefined,
      puActualDatetime: rawQuery.puActualDatetime ? queryInput(rawQuery.puActualDatetime, QueryInputType.OR) : undefined,
      puCompletedDatetime: rawQuery.puCompletedDatetime ? queryInput(rawQuery.puCompletedDatetime, QueryInputType.OR) : undefined,
      puGpsDatetime: rawQuery.puGpsDatetime ? queryInput(rawQuery.puGpsDatetime, QueryInputType.OR) : undefined,
      puLocationAddress: rawQuery.puLocationAddress ? queryInput(rawQuery.puLocationAddress, QueryInputType.OR) : undefined,
      puLocationCityName: rawQuery.puLocationCityName ? queryInput(rawQuery.puLocationCityName, QueryInputType.OR) : undefined,
      puLocationCoordinates: rawQuery.puLocationCoordinates ? queryInput(rawQuery.puLocationCoordinates, QueryInputType.OR) : undefined,
      puLocationId: rawQuery.puLocationId?.length ? queryInput(rawQuery.puLocationId, QueryInputType.OR) : undefined,
      puLocationName: rawQuery.puLocationName ? queryInput(rawQuery.puLocationName, QueryInputType.OR) : undefined,
      puLocationPhoneNumber: rawQuery.puLocationPhoneNumber ? queryInput(rawQuery.puLocationPhoneNumber, QueryInputType.OR) : undefined,
      puLocationStateCode: rawQuery.puLocationStateCode ? queryInput(rawQuery.puLocationStateCode, QueryInputType.OR) : undefined,
      puLocationZipCode: rawQuery.puLocationZipCode ? queryInput(rawQuery.puLocationZipCode, QueryInputType.OR) : undefined,
      rateAmount: rawQuery.rateAmount
        ? queryInput(parseFloat(`${(rawQuery.rateAmount || 0.0) !== 'NO_RATE' ? rawQuery.rateAmount || 0.0 : 0.0}`), QueryInputType.OR)
        : undefined,
      rateCreatedAt: rawQuery.rateCreatedAt ? queryInput(rawQuery.rateCreatedAt, QueryInputType.OR) : undefined,
      rateCreatedBy: rawQuery.rateCreatedBy ? queryInput(rawQuery.rateCreatedBy, QueryInputType.OR) : undefined,
      rateCurrency: rawQuery.rateCurrency ? queryInput(rawQuery.rateCurrency, QueryInputType.OR) : undefined,
      rateEndDatetime: rawQuery.rateEndDatetime ? queryInput(rawQuery.rateEndDatetime, QueryInputType.OR) : undefined,
      rateGroupId: rawQuery.rateGroupId ? queryInput(rawQuery.rateGroupId, QueryInputType.OR) : undefined,
      rateHistoryId: rawQuery.rateHistoryId ? queryInput(rawQuery.rateHistoryId, QueryInputType.OR) : undefined,
      rateId: rawQuery.rateId ? queryInput(rawQuery.rateId, QueryInputType.OR) : undefined,
      rateStartDatetime: rawQuery.rateStartDatetime ? queryInput(rawQuery.rateStartDatetime, QueryInputType.OR) : undefined,
      rateType: rawQuery.rateType ? queryInput(rawQuery.rateType, QueryInputType.OR) : undefined,
      rateUpdatedAt: rawQuery.rateUpdatedAt ? queryInput(rawQuery.rateUpdatedAt, QueryInputType.OR) : undefined,
      rateUpdatedBy: rawQuery.rateUpdatedBy ? queryInput(rawQuery.rateUpdatedBy, QueryInputType.OR) : undefined,
      scheduled: rawQuery.scheduled ? queryInput(rawQuery.scheduled, QueryInputType.RANGE) : undefined,
      scheduledUtc: rawQuery.scheduledUtc ? queryInput(rawQuery.scheduledUtc, QueryInputType.RANGE) : undefined,
      servicerIataAirlineCode: rawQuery.servicerIataAirlineCode
        ? queryInput(rawQuery.servicerIataAirlineCode, QueryInputType.OR)
        : undefined,
      split: rawQuery.split ? queryInput(rawQuery.split, QueryInputType.OR) : undefined,
      status: rawQuery.status ? queryInput(rawQuery.status, QueryInputType.OR) : undefined,
      stopCount: rawQuery.stopCount ? queryInput(rawQuery.stopCount, QueryInputType.OR) : undefined,
      traceId: rawQuery.traceId ? queryInput(rawQuery.traceId, QueryInputType.OR) : undefined,
      trackFlightArrivalGate: rawQuery.trackFlightArrivalGate ? queryInput(rawQuery.trackFlightArrivalGate, QueryInputType.OR) : undefined,
      trackFlightArrivalTerminal: rawQuery.trackFlightArrivalTerminal
        ? queryInput(rawQuery.trackFlightArrivalTerminal, QueryInputType.OR)
        : undefined,
      trackFlightId: rawQuery.trackFlightId ? queryInput(rawQuery.trackFlightId, QueryInputType.OR) : undefined,
      trackFlightLabel: rawQuery.trackFlightLabel ? queryInput(rawQuery.trackFlightLabel, QueryInputType.OR) : undefined,
      trackFlightLastPingTime: rawQuery.trackFlightLastPingTime
        ? queryInput(rawQuery.trackFlightLastPingTime, QueryInputType.OR)
        : undefined,
      trackFlightOrigin: rawQuery.trackFlightOrigin ? queryInput(rawQuery.trackFlightOrigin, QueryInputType.OR) : undefined,
      type: rawQuery.type?.length ? queryInput(rawQuery.type, QueryInputType.OR) : undefined,
      updatedAt: rawQuery.updatedAt ? queryInput(rawQuery.updatedAt, QueryInputType.OR) : undefined,
      updatedBy: rawQuery.updatedBy ? queryInput(rawQuery.updatedBy, QueryInputType.OR) : undefined,
      vehicleCapacity: rawQuery.vehicleCapacity ? queryInput(rawQuery.vehicleCapacity, QueryInputType.OR) : undefined,
      vehicleId: rawQuery.vehicleId ? queryInput(rawQuery.vehicleId, QueryInputType.OR) : undefined,
      vehicleMake: rawQuery.vehicleMake ? queryInput(rawQuery.vehicleMake, QueryInputType.OR) : undefined,
      vehicleModel: rawQuery.vehicleModel ? queryInput(rawQuery.vehicleModel, QueryInputType.OR) : undefined,
      vehiclePhoneNumber: rawQuery.vehiclePhoneNumber ? queryInput(rawQuery.vehiclePhoneNumber, QueryInputType.OR) : undefined,
      vehicleTrackingId: rawQuery.vehicleTrackingId ? queryInput(rawQuery.vehicleTrackingId, QueryInputType.OR) : undefined,
    }))
    .reduce((acc, curr) => {
      const formattedQuery = Object.entries(curr).reduce((formattedAcc, [key, value]) => {
        if (value !== undefined) formattedAcc[key] = value;
        return formattedAcc;
      }, {});
      acc.push(formattedQuery);
      return acc;
    }, []);
};

export const columns = {
  accidentIssueCount: null,
  actual: null,
  airportCode: null,
  attendants: null,
  cachedDoLocationId: null,
  cachedDriverId: null,
  cachedPayerProviderId: null,
  cachedPuLocationId: null,
  cachedVehicleId: null,
  combineId: null,
  combineType: null,
  communicationCount: null,
  companyId: null,
  completionComment: null,
  completionCost: null,
  completionCreatedAt: null,
  completionCreatedBy: null,
  completionCurrency: null,
  completionDatetime: null,
  completionDistanceMeters: null,
  completionEfficiency: null,
  completionEquivalency: null,
  completionId: null,
  completionTypeDisplayName: null,
  completionTypeId: null,
  completionUpdatedAt: null,
  completionUpdatedBy: null,
  createdAt: null,
  createdBy: null,
  crewId: null,
  curbsideAt: null,
  deletedAt: null,
  deletedBy: null,
  dispatchIssueCount: null,
  doActualDatetime: null,
  doAirportCode: null,
  doLocationAddress: null,
  doLocationCityName: null,
  doLocationCoordinates: null,
  doLocationId: null,
  doLocationName: null,
  doLocationPhoneNumber: null,
  doLocationStateCode: null,
  doLocationZipCode: null,
  driverAvatar: null,
  driverCrewCount: null,
  driverEmployeeId: null,
  driverFirstName: null,
  driverId: null,
  driverLastName: null,
  etaMinutes: null,
  fcrCount: null,
  firstName: null,
  flagColors: null,
  flightNumber: null,
  fromManifest: null,
  hasPaperwork: null,
  id: null,
  importTime: null,
  incidentIssueCount: null,
  isIllegalCombine: null,
  kind: null,
  lastName: null,
  latestScheduled: null,
  latestScheduledUtc: null,
  legacyId: null,
  loopName: null,
  lostAndFoundIssueCount: null,
  manifestImportId: null,
  offset: null,
  payerProviderDisplayName: null,
  payerProviderId: null,
  pilots: null,
  plannedAt: null,
  providerId: null,
  puActualDatetime: null,
  puCompletedDatetime: null,
  puGpsDatetime: null,
  puLocationAddress: null,
  puLocationCityName: null,
  puLocationCoordinates: null,
  puLocationId: null,
  puLocationName: null,
  puLocationPhoneNumber: null,
  puLocationStateCode: null,
  puLocationZipCode: null,
  rateAmount: null,
  rateCreatedAt: null,
  rateCreatedBy: null,
  rateCurrency: null,
  rateEndDatetime: null,
  rateGroupId: null,
  rateHistoryId: null,
  rateId: null,
  rateStartDatetime: null,
  rateType: null,
  rateUpdatedAt: null,
  rateUpdatedBy: null,
  scheduled: null,
  scheduledUtc: null,
  servicerIataAirlineCode: null,
  split: null,
  status: null,
  stopCount: null,
  traceId: null,
  trackFlightArrivalGate: null,
  trackFlightArrivalTerminal: null,
  trackFlightId: null,
  trackFlightLabel: null,
  trackFlightLastPingTime: null,
  trackFlightOrigin: null,
  type: null,
  updatedAt: null,
  updatedBy: null,
  vehicleCapacity: null,
  vehicleId: null,
  vehicleMake: null,
  vehicleModel: null,
  vehiclePhoneNumber: null,
  vehicleTrackingId: null,
};

export const customColumns: TripTripWithCompletionAndRateCsvColumn[] = [
  {
    displayName: 'TYPE',
    type: TripTripWithCompletionAndRateCsvColumnEnum.Type,
  },
  {
    displayName: 'DATE',
    type: TripTripWithCompletionAndRateCsvColumnEnum.Scheduled,
    format: DatetimeFormatEnum.DfDate,
  },
  {
    displayName: 'SCH',
    type: TripTripWithCompletionAndRateCsvColumnEnum.Scheduled,
    format: DatetimeFormatEnum.DfTime,
  },
  {
    displayName: 'CURB',
    type: TripTripWithCompletionAndRateCsvColumnEnum.ImportTime,
    format: DatetimeFormatEnum.DfTime,
  },
  {
    displayName: 'ACT',
    type: TripTripWithCompletionAndRateCsvColumnEnum.Actual,
    format: DatetimeFormatEnum.DfTime,
  },
  {
    displayName: 'CITY',
    type: TripTripWithCompletionAndRateCsvColumnEnum.AirportCode,
  },
  {
    displayName: 'AL',
    type: TripTripWithCompletionAndRateCsvColumnEnum.ServicerIataAirlineCode,
  },
  {
    displayName: 'FLT',
    type: TripTripWithCompletionAndRateCsvColumnEnum.FlightNumber,
  },
  {
    displayName: 'PLT',
    type: TripTripWithCompletionAndRateCsvColumnEnum.Pilots,
  },
  {
    displayName: 'FA',
    type: TripTripWithCompletionAndRateCsvColumnEnum.Attendants,
  },
  {
    displayName: 'PASSENGER COUNT',
    type: TripTripWithCompletionAndRateCsvColumnEnum.DriverCrewCount,
  },
  {
    displayName: 'DRV FIRST NAME',
    type: TripTripWithCompletionAndRateCsvColumnEnum.DriverFirstName,
  },
  {
    displayName: 'DRV LAST NAME',
    type: TripTripWithCompletionAndRateCsvColumnEnum.DriverLastName,
  },
  {
    displayName: 'VAN',
    type: TripTripWithCompletionAndRateCsvColumnEnum.VehicleTrackingId,
  },
  {
    displayName: 'PICKUP',
    type: TripTripWithCompletionAndRateCsvColumnEnum.PuLocationName,
  },
  {
    displayName: 'DROPOFF',
    type: TripTripWithCompletionAndRateCsvColumnEnum.DoLocationName,
  },
  {
    displayName: 'CLT',
    type: TripTripWithCompletionAndRateCsvColumnEnum.PayerProviderDisplayName,
  },
  {
    displayName: 'RFP',
    type: TripTripWithCompletionAndRateCsvColumnEnum.PuActualDatetime,
  },
  {
    displayName: 'PU',
    type: TripTripWithCompletionAndRateCsvColumnEnum.PuCompletedDatetime,
  },
  {
    displayName: 'COMPL',
    type: TripTripWithCompletionAndRateCsvColumnEnum.CompletionDatetime,
    format: DatetimeFormatEnum.DfDatetime,
  },
  {
    displayName: 'DRV_TIME',
    type: TripTripWithCompletionAndRateCsvColumnEnum.EtaMinutes,
  },
  {
    displayName: 'C.TYPE',
    type: TripTripWithCompletionAndRateCsvColumnEnum.CompletionTypeDisplayName,
  },
];

export default downloadTripWithCompletionAndRateAsCSV;
